import { Component, HostListener, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { CostAdjustmentDetailTableModel } from 'src/app/models/tables/inventory/cost-adjustment-detail.table.model';
import { CostAdjustmentTableModel } from 'src/app/models/tables/inventory/cost-adjustment.table.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { CostAdjustmentDetailService } from 'src/app/services/inventory/cost-adjustment-detail.service';
import { CostAdjustmentService } from 'src/app/services/inventory/cost-adjustment.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-cost-adjustment',
  templateUrl: './cost-adjustment.component.html',
  styleUrls: ['./cost-adjustment.component.scss'],
  standalone: false
})
export class CostAdjustmentComponent implements OnInit {

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  detailData: PagedResponseModel<CostAdjustmentDetailTableModel> = new PagedResponseModel<CostAdjustmentDetailTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = []
  generalData: PagedResponseModel<CostAdjustmentTableModel> = new PagedResponseModel<CostAdjustmentTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined
  limit: number = 10;
  nameWindow: string = 'Ajuste Costos';
  page: number = 1;
  permissionUpdatable: boolean = false;
  previousSort: { field: string; isAscsendant: number };
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }
  ];
  url: string = 'cost-adjustment';

  constructor(private costAdjustmentService: CostAdjustmentService,
    private costAdjustmentDetailService: CostAdjustmentDetailService,
    private router: Router,
    private headerService: HeaderService,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private windowService: WindowService) { }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(this.url);
    if (filters) this.filters = JSON.parse(filters);
    this.getPermission();
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllCostAdjustmentsWithPagination(),
    ]);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  createNewRegister() {
    if (this.permissionUpdatable) {
      this.router.navigate([`/${this.url}/register`]);
    } else {
      this.toastHandlerService.showToast('error', `El usuario no tiene permisos para crear ajustes de costos.
        Comuníquese con el administrador del sistema.`
      );
    }
  }

  async exportDetailConsultation() {
    try {
      const blob = await firstValueFrom(this.costAdjustmentDetailService.exportDetailConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'AjusteCostosDetalle.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar los detalles del ajuste de costos.', error);
    }
  }

  async exportGeneralConsultation() {
    try {
      const blob = await firstValueFrom(this.costAdjustmentService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'AjusteCostos.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar los ajustes costos.', error);
    }
  }

  async getAllCostAdjustmentsWithPagination() {
    try {
      const response = await firstValueFrom(this.costAdjustmentService.getAllCostAdjustmentsWithPagination(this.page,
        this.limit, this.columnSort, this.ascendant, this.filters));
      this.generalData = response;
    } catch (error) {
      console.error('Error al obtener los ajustes de costos.', error);
    }
  }

  async getAllCostAdjustmentDetailsWithPagination() {
    try {
      const response = await firstValueFrom(this.costAdjustmentDetailService.getAllCostAdjustmentDetailsWithPagination(this.page,
        this.limit, this.columnSort, this.ascendant, this.filters));
      this.detailData = response;
    } catch (error) {
      console.error('Error al obtener el detalle de los ajustes de costos.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        const response = await firstValueFrom(this.costAdjustmentDetailService.getColumnsForDetailConsultation());
        this.detailHeader = response;
      }
    } catch (error) {
      console.error('Error al obtener las columnas para la consulta de detalle.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        const response = await firstValueFrom(this.costAdjustmentService.getColumnsForGeneralConsultation());
        this.generalHeader = response;
      }
    } catch (error) {
      console.error('Error al obtener las columnas para la consulta general.', error);
    }
  }

  async getPermission() {
    await this.getPermissionProfileByUrl();
    const url = this.router.url as string
    this.url = url.substring(1);
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('error', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`);
      }
    } else {
      localStorage.clear();
    }
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.permissionUpdatable = response;
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getAllCostAdjustmentsWithPagination()
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getAllCostAdjustmentDetailsWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  onAction(event: { action: string, item?: CostAdjustmentTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/${this.url}/register/${event.item.costAdjustmentId}`]);
    }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.getColumnsForDetailConsultation();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getAllCostAdjustmentsWithPagination()
        break;
      case 1:
        await this.getAllCostAdjustmentDetailsWithPagination()
        break;
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
